import { FileUp, FolderOpen, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { formatDate } from '../../model/dates';
import { displayName, lifeSpan } from '../../model/people';
import { generateLargeTree, loadSampleTree } from '../../model/sample';
import { SEARCH_HELP } from '../../model/search';
import { mergePeople, mergeWouldCreateCycle, newTree } from '../../model/tree';
import type { Id, Tree } from '../../model/types';
import { isDark, useSystemDark } from '../appearance';
import { adoptTree, closeDialog, commit, confirmAction, focusPerson, getState, lastBackup, openTree, removeTree, replaceDialog, requestPersistence, setState, toast, updateSettings, useApp, type Dialog } from '../store';
import { findTheme, forcedDark, paletteOf, paletteTokens, PRESETS, PRIDE, stripeGradient, type Theme } from '../themes';
import { Avatar, Modal } from './common';
import { PersonPicker } from './PersonPicker';
import { SyncSettings } from './SyncDialog';

export function ConfirmDialog({ d }: { d: Extract<Dialog, { type: 'confirm' }> }) {
  return (
    <Modal
      title={d.title}
      footer={
        <>
          <button className="btn" onClick={closeDialog} autoFocus>
            Cancel
          </button>
          <button
            className={`btn ${d.danger ? 'danger solid' : 'primary'}`}
            onClick={() => {
              closeDialog();
              d.onConfirm();
            }}
          >
            {d.confirmLabel}
          </button>
        </>
      }
    >
      <p>{d.body}</p>
    </Modal>
  );
}

export function TreesDialog() {
  const trees = useApp((s) => s.trees);
  const current = useApp((s) => s.tree);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [name, setName] = useState('');
  return (
    <Modal
      title="Your trees"
      footer={
        <>
          <button
            className="btn left"
            onClick={async () => {
              await adoptTree(await loadSampleTree());
              closeDialog();
            }}
          >
            Open sample
          </button>
          <button className="btn" onClick={() => replaceDialog({ type: 'import', mode: 'new' })} title="Open a GEDCOM, MacFamilyTree, or Relationships file as a new tree">
            <FileUp size={15} /> Import file
          </button>
          <button
            className="btn primary"
            onClick={async () => {
              await adoptTree(newTree('New family tree'));
              closeDialog();
            }}
          >
            <Plus size={15} /> New tree
          </button>
        </>
      }
    >
      <p className="faint small-text" style={{ marginBottom: 10 }}>
        Trees are saved in this browser only. Export a Relationships file to back up or move a tree to another device, and import it there.
      </p>
      <div className="stack tight">
        {trees.map((t) => (
          <div key={t.id} className="row" style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 10, background: t.id === current?.id ? 'var(--accent-soft)' : undefined }}>
            {renaming === t.id ? (
              <form
                className="row"
                style={{ flex: 1 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (current?.id === t.id) commit({ ...current, name: name.trim() || current.name });
                  setRenaming(null);
                }}
              >
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus aria-label="Tree name" />
                <button className="btn small primary" type="submit">
                  Save
                </button>
              </form>
            ) : (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 650 }}>{t.name}</div>
                <div className="faint small-text">
                  {t.people.toLocaleString('en-US')} people · edited {formatDate(isoToGedcom(t.updatedAt))}
                </div>
              </div>
            )}
            {t.id === current?.id ? (
              renaming !== t.id && (
                <button className="btn small ghost" onClick={() => { setRenaming(t.id); setName(t.name); }}>
                  <Pencil size={14} /> Rename
                </button>
              )
            ) : (
              <button
                className="btn small"
                onClick={async () => {
                  await openTree(t.id);
                  closeDialog();
                }}
              >
                <FolderOpen size={14} /> Open
              </button>
            )}
            <button
              className="btn icon small ghost danger"
              aria-label={`Delete ${t.name}`}
              onClick={() =>
                confirmAction({
                  title: `Delete “${t.name}”?`,
                  body: 'This permanently deletes the tree from this browser. It cannot be undone. Export it first if you might want it later.',
                  confirmLabel: 'Delete tree',
                  danger: true,
                  onConfirm: () => removeTree(t.id),
                })
              }
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {!trees.length && <p className="muted">No saved trees yet.</p>}
      </div>
    </Modal>
  );
}

function isoToGedcom(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()} ${['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][d.getMonth()]} ${d.getFullYear()}`;
}

export function SettingsDialog() {
  const settings = useApp((s) => s.settings);
  const systemDark = useSystemDark();
  const theme = findTheme(settings.themeId);
  const forced = forcedDark(theme);
  const dark = isDark(settings, systemDark);
  return (
    <Modal title="Settings" wide footer={<button className="btn primary" onClick={closeDialog}>Done</button>}>
      <div className="stack">
        <fieldset className="settings-group">
          <legend className="field-label">Appearance</legend>
          <div className="mode-options">
            {(
              [
                { value: 'system', label: 'Automatic', hint: 'Follows your device' },
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
              ] as const
            ).map((m) => (
              <label key={m.value} className="mode-option">
                <input type="radio" name="theme-mode" checked={settings.theme === m.value} disabled={forced !== null} onChange={() => updateSettings({ theme: m.value })} />
                <span>
                  <b>{m.label}</b>
                  {'hint' in m && <small>{m.hint}</small>}
                </span>
              </label>
            ))}
          </div>
          {forced !== null && (
            <p className="faint small-text" style={{ marginTop: 8 }}>
              {theme.name} only comes {forced ? 'dark' : 'light'}, so the app is {forced ? 'dark' : 'light'} while it's chosen.
            </p>
          )}
        </fieldset>
        <fieldset className="settings-group">
          <legend className="field-label">Theme</legend>
          <ThemeGrid themes={PRESETS} label="Themes" current={theme.id} dark={dark} />
          <details className="settings-fold" open={theme.id.startsWith('pride-') || undefined}>
            <summary>
              Pride themes <span className="faint">{PRIDE.length}</span>
            </summary>
            <ThemeGrid themes={PRIDE} label="Pride themes" current={theme.id} dark={dark} />
          </details>
        </fieldset>
        <fieldset className="settings-group">
          <legend className="field-label">Places</legend>
          <label className="mode-option">
            <input type="checkbox" checked={settings.placeSearch !== false} onChange={(e) => updateSettings({ placeSearch: e.target.checked })} />
            <span>
              <b>Use OpenStreetMap</b>
              <small>
                Place fields suggest places already in your trees as you type, without sending anything, and the Map can always use the outline built into the app.
                With this on, place fields also offer to look up what you’ve typed on OpenStreetMap, the Map offers to find where your places are (either sends just the
                place names, and only when you choose to), and the Map can show OpenStreetMap’s street map, which sees which part of the world it’s showing.
              </small>
            </span>
          </label>
        </fieldset>
        <StorageSettings />
        <SyncSettings />
        <details className="settings-fold">
          <summary>Developer tools</summary>
          <p className="faint small-text" style={{ marginBottom: 8 }}>
            Generate a large made-up tree to test speed. Each opens as a new tree; your trees aren't changed.
          </p>
          <div className="row wrap">
            {[500, 2000, 10000].map((n) => (
              <button
                key={n}
                className="btn small"
                onClick={async () => {
                  await adoptTree(generateLargeTree(n));
                  closeDialog();
                }}
              >
                Generate {n.toLocaleString('en-US')}-person test tree
              </button>
            ))}
          </div>
        </details>
      </div>
    </Modal>
  );
}

/** Whether the browser will keep the trees, and when the open one was last saved to a file. */
function StorageSettings() {
  const tree = useApp((s) => s.tree);
  const persistence = useApp((s) => s.persistence);
  const memory = useApp((s) => s.saveState === 'memory');
  const [refused, setRefused] = useState(false);
  const backup = tree ? lastBackup(tree.id) : undefined;
  const ask = async () => setRefused((await requestPersistence()) !== 'granted');
  return (
    <fieldset className="settings-group">
      <legend className="field-label">Storage</legend>
      <div className="stack tight small-text">
        {memory ? (
          <p>Your browser isn't allowing storage, so nothing is kept after this tab closes.</p>
        ) : persistence === 'granted' ? (
          <p>Your browser will keep your trees until you delete them.</p>
        ) : persistence === 'not-granted' ? (
          <div>
            <p>
              Your browser may clear your trees to free up space, and Safari clears them after a week without a visit. Adding Relationships to your home screen or dock, or letting it keep
              its storage, prevents that.
            </p>
            <div className="row wrap" style={{ marginTop: 8 }}>
              <button className="btn small" onClick={ask}>
                Keep my trees
              </button>
              {refused && <span className="faint">Your browser didn't allow it. Browsers usually do once the app is installed or used often.</span>}
            </div>
          </div>
        ) : (
          <p>Your trees are kept in this browser.</p>
        )}
        {tree && (
          <p>
            Last backup of “{tree.name}”:{' '}
            {backup ? (
              <>
                {formatDate(isoToGedcom(backup.at))}
                {backup.updatedAt !== tree.updatedAt && <span className="faint"> (changed since)</span>}
              </>
            ) : (
              <span className="faint">none made in this browser</span>
            )}
          </p>
        )}
      </div>
    </fieldset>
  );
}

/** Theme cards: each a tiny family chart in its colors, its name under it. */
function ThemeGrid({ themes, label, current, dark }: { themes: Theme[]; label: string; current: string; dark: boolean }) {
  return (
    <div className="theme-grid" role="group" aria-label={label}>
      {themes.map((th) => {
        const tk = paletteTokens(paletteOf(th, dark));
        const card = { background: tk['--card-bg'], boxShadow: `0 0 0 1px ${tk['--border']}` };
        return (
          <button key={th.id} type="button" className="theme-card" aria-pressed={th.id === current} title={th.name} onClick={() => updateSettings({ themeId: th.id })}>
            <span className="theme-swatch" style={{ background: tk['--bg'] }} aria-hidden>
              {th.stripes && <span className="sw-stripes" style={{ background: stripeGradient(th.stripes) }} />}
              <span className="sw-couple">
                <span className="sw-card" style={card}>
                  <i style={{ background: tk['--accent'] }} />
                  <span style={{ background: tk['--text'] }} />
                </span>
                <span className="sw-partner" style={{ background: tk['--line-partner'] }} />
                <span className="sw-card" style={card}>
                  <i style={{ background: tk['--line-chosen'] }} />
                  <span style={{ background: tk['--text-muted'] }} />
                </span>
              </span>
              <span className="sw-drop" style={{ background: tk['--line'] }} />
              <span className="sw-card sw-child" style={{ ...card, boxShadow: `0 0 0 2px ${tk['--accent']}` }}>
                <i style={{ background: tk['--line-foster'] }} />
                <span style={{ background: tk['--text'] }} />
              </span>
            </span>
            <span className="theme-name">{th.name}</span>
          </button>
        );
      })}
    </div>
  );
}

const SHORTCUTS: [string, string][] = [
  ['⌘K / Ctrl+K', 'Find a person or run a command'],
  ['/', 'Search people'],
  ['⌘Z / Ctrl+Z', 'Undo'],
  ['⇧⌘Z / Ctrl+Y', 'Redo'],
  ['N', 'Add a person'],
  ['1–9, 0', 'Switch views, in sidebar order (Tree, Network, People, Groups, Relate, Reports, Countdowns, Map, Media, Sources)'],
  ['Arrow keys', 'Move between people in the tree chart'],
  ['Enter', 'Center the chart on the selected person'],
  ['+ / − / 0', 'Zoom in, zoom out, fit (in charts)'],
  ['E', 'Edit the selected person'],
  ['Space', 'In People, tick the focused row (Shift+click a box ticks a range); Esc clears'],
  ['Esc', 'Close dialogs and panels'],
  ['?', 'Show this help'],
];

export function HelpDialog() {
  return (
    <Modal title="Help" wide footer={<button className="btn primary" onClick={closeDialog}>Got it</button>}>
      <div className="stack">
        <div>
          <div className="section-title" style={{ marginBottom: 6 }}>Getting around</div>
          <ul style={{ margin: 0, paddingLeft: 18 }} className="small-text">
            <li><b>Tree</b> shows a family chart around one person. Click someone for details; double-click (or the ▲/▼ buttons) to re-center. Drag or scroll to pan; pinch, ⌘/Ctrl+scroll, or the buttons to zoom.</li>
            <li><b>Network</b> shows everyone connected by family, friendship, chosen family, and groups.</li>
            <li><b>People</b> is a searchable, sortable list of everyone. Tick people to edit, merge, or delete several at once.</li>
            <li><b>Relate</b> tells you exactly how two people are related, with the path between them.</li>
            <li><b>Groups</b> holds friend groups, found families, households, and communities.</li>
            <li><b>Reports</b> shows facts and charts about the tree.</li>
            <li><b>Countdowns</b> lists birthdays, anniversaries, and remembrance days coming up.</li>
            <li><b>Map</b> shows where people were born, lived, partnered, and died. Click a pin to see who and what.</li>
            <li>Right-click a person, or empty space, for a menu of things you can do there.</li>
          </ul>
        </div>
        <div>
          <div className="section-title" style={{ marginBottom: 6 }}>Keyboard shortcuts</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px' }} className="small-text">
            {SHORTCUTS.map(([k, v]) => (
              <div key={k} style={{ display: 'contents' }}>
                <span><kbd>{k}</kbd></span>
                <span>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="section-title" style={{ marginBottom: 6 }}>Search syntax</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px' }} className="small-text">
            {SEARCH_HELP.map((h) => (
              <div key={h.syntax} style={{ display: 'contents' }}>
                <code>{h.syntax}</code>
                <span className="muted">{h.meaning}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="callout small-text">
          Your data stays in this browser. Nothing is sent to a server, except places you choose to look up on OpenStreetMap, and which part of the world the Map’s street map is showing. Use <b>Export</b> regularly to keep a backup.
        </div>
      </div>
    </Modal>
  );
}

export function MergeDialog({ personId, otherId }: { personId: Id; otherId?: Id }) {
  const tree = useApp((s) => s.tree)!;
  const [other, setOther] = useState<Id | null>(otherId ?? null);
  const p = tree.people[personId];
  if (!p) return null;
  // Someone and their own grandchild (or further) can't be the same person.
  const loop = !!other && mergeWouldCreateCycle(tree, personId, other);
  return (
    <Modal
      title={`Merge ${displayName(p)} with…`}
      wide
      footer={
        <>
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!other || loop}
            onClick={() => {
              if (!other || loop) return;
              commit(mergePeople(getState().tree!, personId, other));
              setState((s) => ({ dialogs: s.dialogs.filter((d) => d.type !== 'merge' && d.type !== 'edit-person') }));
              focusPerson(personId);
              toast('Merged. You can undo this.', 'success');
            }}
          >
            Merge
          </button>
        </>
      }
    >
      <div className="stack merge-body">
        <p className="muted">Use this when the same person was entered twice. The duplicate's names, events, tags, notes, relationships, and groups move onto {displayName(p)}, and the duplicate is removed.</p>
        <PersonPicker tree={tree} value={other} onChange={setOther} exclude={[personId]} autoFocus placeholder="Find the duplicate…" ariaLabel="Duplicate person" />
        {loop && (
          <div className="callout danger">
            {displayName(tree.people[other!])} is {displayName(p)}’s ancestor or descendant, two or more generations apart, so they can’t be the same person: merging them would make someone their own ancestor.
          </div>
        )}
        {other && tree.people[other] && (
          <div className="merge-compare">
            <MergeCard tree={tree} id={personId} role="Keeps this record" />
            <span className="merge-arrow" aria-hidden>←</span>
            <MergeCard tree={tree} id={other} role="Merged in, then removed" />
          </div>
        )}
      </div>
    </Modal>
  );
}

function MergeCard({ tree, id, role }: { tree: Tree; id: Id; role: string }) {
  const p = tree.people[id];
  const rels = Object.values(tree.relationships).filter((r) => r.a === id || r.b === id).length;
  const groups = Object.values(tree.groups).filter((g) => g.members.some((m) => m.personId === id)).length;
  return (
    <div className="merge-card">
      <div className="faint small-text">{role}</div>
      <div className="row" style={{ marginTop: 6 }}>
        <Avatar person={p} />
        <div style={{ minWidth: 0 }}>
          <b>{displayName(p)}</b>
          <div className="faint small-text">{lifeSpan(p) || 'No dates'}</div>
        </div>
      </div>
      <div className="small-text muted" style={{ marginTop: 8 }}>
        {p.names.length} {p.names.length === 1 ? 'name' : 'names'} · {p.events.length} {p.events.length === 1 ? 'event' : 'events'} · {rels} {rels === 1 ? 'relationship' : 'relationships'}
        {groups ? ` · ${groups} ${groups === 1 ? 'group' : 'groups'}` : ''}
      </div>
    </div>
  );
}
